import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { SuggestionCard } from "@evyely/ui";
import { TranscriptFeed } from "../components/overlay/TranscriptFeed";
import { useAuthToken } from "../lib/authToken";
import { WS_URL } from "../lib/env";
export default function OverlayPage() {
    const getAuthToken = useAuthToken();
    const wsRef = useRef(null);
    const [tab, setTab] = useState("ai");
    const [transcripts, setTranscripts] = useState([]);
    const [suggestion, setSuggestion] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const sessionMode = (new URLSearchParams(window.location.search).get("mode") === "interview"
        ? "interview"
        : "meeting");
    useEffect(() => {
        document.body.classList.add("overlay-mode");
        document.documentElement.classList.add("overlay-mode");
        document.body.style.background = "transparent";
        document.body.style.backgroundColor = "transparent";
        document.documentElement.style.background = "transparent";
        document.documentElement.style.backgroundColor = "transparent";
        const root = document.getElementById("root");
        if (root) {
            root.style.background = "transparent";
            root.style.backgroundColor = "transparent";
        }
        return () => {
            document.body.classList.remove("overlay-mode");
            document.documentElement.classList.remove("overlay-mode");
        };
    }, []);
    useEffect(() => {
        let ws;
        async function connect() {
            const wsBase = `${WS_URL}/ws`;
            const token = await getAuthToken();
            const wsUrl = new URL(wsBase);
            if (token)
                wsUrl.searchParams.set("token", token);
            ws = new WebSocket(wsUrl.toString());
            wsRef.current = ws;
            ws.onopen = () => {
                setIsConnected(true);
                ws.send(JSON.stringify({ type: "start_session", platform: "desktop", mode: sessionMode }));
            };
            ws.onmessage = (e) => {
                const msg = JSON.parse(e.data);
                if (msg.type === "transcript")
                    setTranscripts((p) => [...p.slice(-50), msg.entry]);
                if (msg.type === "suggestion_start") {
                    setSuggestion("");
                    setIsStreaming(true);
                }
                if (msg.type === "suggestion_token")
                    setSuggestion((p) => p + msg.token);
                if (msg.type === "suggestion_end")
                    setIsStreaming(false);
            };
            ws.onclose = () => setIsConnected(false);
        }
        void connect();
        // Receive audio from Electron main process
        window.electron?.onAudioChunk((data) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "audio_chunk", data }));
            }
        });
        // Receive OCR text from Electron main process
        window.electron?.onOCRUpdate((text) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: "ocr_text", text }));
            }
        });
        return () => {
            ws?.close();
        };
    }, [getAuthToken, sessionMode]);
    const isElectron = navigator.userAgent.toLowerCase().includes("electron") || !!window.electron;
    return (_jsxs("div", { style: {
            width: "100vw", height: "100vh",
            background: isElectron ? "transparent" : "radial-gradient(ellipse at 25% 35%, #1a1340 0%, #0A0E27 60%, #04061A 100%)",
            display: "flex",
            alignItems: isElectron ? "flex-start" : "center",
            justifyContent: isElectron ? "flex-start" : "center",
            padding: isElectron ? 10 : 0,
            boxSizing: "border-box",
        }, children: [_jsx("style", { children: `
        @keyframes evyOverlayPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0; transform: scale(1.6); }
        }
      ` }), !isElectron && (_jsx("div", { style: {
                    position: "absolute", inset: 0,
                    backgroundImage: `
            radial-gradient(ellipse at 20% 50%, rgba(139,92,246,0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 20%, rgba(59,130,246,0.1) 0%, transparent 50%)
          `,
                    pointerEvents: "none",
                } })), _jsxs("div", { style: {
                    background: "rgba(7, 7, 18, 0.62)",
                    backdropFilter: "blur(32px) saturate(190%)",
                    WebkitBackdropFilter: "blur(32px) saturate(190%)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 18,
                    padding: "12px 14px 14px",
                    display: "flex",
                    flexDirection: "column",
                    width: isElectron ? "100%" : 360,
                    height: isElectron ? "100%" : 480,
                    boxShadow: "0 28px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(109,93,252,0.10) inset, inset 0 1px 0 rgba(255,255,255,0.06)",
                    overflow: "hidden",
                    position: "relative",
                }, children: [_jsx("div", { "aria-hidden": true, style: {
                            position: "absolute", top: -40, right: -40, width: 200, height: 200,
                            background: "radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)",
                            filter: "blur(20px)", pointerEvents: "none",
                        } }), _jsxs("div", { style: {
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            marginBottom: 12,
                            WebkitAppRegion: "drag",
                            cursor: "default",
                        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [_jsxs("span", { style: { position: "relative", display: "inline-flex" }, children: [_jsx("span", { style: {
                                                    width: 6, height: 6, borderRadius: "50%",
                                                    background: isConnected ? "#22c55e" : "rgba(255,255,255,0.18)",
                                                    boxShadow: isConnected ? "0 0 8px rgba(34,197,94,0.7)" : "none",
                                                    position: "relative", zIndex: 1,
                                                } }), isConnected && (_jsx("span", { style: {
                                                    position: "absolute", inset: -4, borderRadius: 999,
                                                    background: "#22c55e", opacity: 0.4,
                                                    animation: "evyOverlayPulse 1.8s ease-in-out infinite",
                                                } }))] }), _jsx("span", { style: {
                                            fontSize: 10, color: "rgba(255,255,255,0.5)",
                                            fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em",
                                            fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
                                        }, children: "Evyely" })] }), _jsx("div", { style: {
                                    display: "flex", gap: 1,
                                    background: "rgba(255,255,255,0.04)",
                                    borderRadius: 7, padding: "2px",
                                    WebkitAppRegion: "no-drag",
                                }, children: ["ai", "transcript"].map((tabId) => {
                                    const active = tab === tabId;
                                    return (_jsx("button", { onClick: () => setTab(tabId), "aria-label": tabId === "ai" ? "AI suggestions" : "Transcript", style: {
                                            background: active ? "rgba(255,255,255,0.12)" : "transparent",
                                            border: "none",
                                            color: active ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.45)",
                                            padding: "5px 10px",
                                            borderRadius: 6,
                                            fontSize: 11,
                                            cursor: "pointer",
                                            fontWeight: 600,
                                            fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
                                            transition: "all 160ms ease",
                                            letterSpacing: "0.04em",
                                            display: "inline-flex", alignItems: "center", gap: 5,
                                        }, children: tabId === "ai" ? (_jsxs(_Fragment, { children: [_jsx("svg", { width: "10", height: "10", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M12 2L13.8 8.5L20 10L13.8 11.5L12 18L10.2 11.5L4 10L10.2 8.5L12 2Z" }) }), "AI"] })) : (_jsx("svg", { width: "11", height: "11", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M4 6h16M4 12h16M4 18h10", stroke: "currentColor", strokeWidth: "2.4", strokeLinecap: "round" }) })) }, tabId));
                                }) })] }), _jsx("div", { style: { flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }, children: tab === "ai" ? (_jsx(_Fragment, { children: suggestion || isStreaming ? (_jsx(SuggestionCard, { suggestion: suggestion, isStreaming: isStreaming })) : (_jsxs("div", { style: {
                                    flex: 1, display: "flex", flexDirection: "column",
                                    alignItems: "center", justifyContent: "center", gap: 10,
                                }, children: [_jsx("div", { style: {
                                            width: 38, height: 38, borderRadius: 19,
                                            background: "rgba(167,139,250,0.10)",
                                            border: "1px solid rgba(167,139,250,0.18)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: "rgba(167,139,250,0.85)",
                                        }, children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M12 2L13.8 8.5L20 10L13.8 11.5L12 18L10.2 11.5L4 10L10.2 8.5L12 2Z" }) }) }), _jsxs("p", { style: {
                                            color: "rgba(255,255,255,0.42)", fontSize: 12, margin: 0,
                                            textAlign: "center", fontWeight: 500, lineHeight: 1.55,
                                            fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
                                        }, children: ["Suggestions appear", _jsx("br", {}), _jsx("span", { style: { color: "rgba(255,255,255,0.22)" }, children: "as you speak" })] })] })) })) : (_jsx("div", { style: { overflowY: "auto", flex: 1 }, children: _jsx(TranscriptFeed, { entries: transcripts }) })) })] })] }));
}
